import { type ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-quartz.css';
import '@/styles/neon-data-grid.css';
import '@/styles/plans-of-study.grid.css';
import './subject-areas.grid.scss';
import { NeonButton, NeonCardStandard, NeonErrorPageContent, NeonIcon } from '@ps-refarch-ux/neon';
import { DATA_SLOT, NeonButtonTypes } from '@/constants/neon-constants';
import { neonFontSizeVar, neonLineHeightVar } from '@/utils/neon-utils';
import {
  getSubjectAreasBreadcrumbs,
  SCEDSubjectCodes,
  SUBJECT_AREAS_GRID_HEADER_CLASS,
  SUBJECT_AREAS_ICON,
  SUBJECT_AREAS_ID,
  SUBJECT_AREAS_PAGINATION_THRESHOLD,
  SUBJECT_AREAS_SKELETON_COLUMNS,
  SUBJECT_AREAS_TEXT,
} from '@/constants/subject-area-constants';
import { EmptyState } from '@/components/EmptyState/empty-state';
import {
  actionButtonsContainerStyle,
  contentStackStyle,
  createMarginStyle,
  infoRowStyle,
  paragraphStyle,
  sectionSpacingStyle,
  tableColumnsStyle,
} from './subject-areas.styles';
import { SortableHeader } from '@/components/sortable-header';
import {
  useCreateSubjectArea,
  useDeleteSubjectArea,
  useUpdateSubjectArea,
  useGetSubjectAreas,
} from '@/repositories';
import type { SubjectArea } from '@/types';
import { AllCommunityModule, ModuleRegistry } from 'ag-grid-community';
import type {
  CellStyle,
  ColDef,
  ColumnState,
  IHeaderParams,
  SortChangedEvent,
} from 'ag-grid-community';
import { NeonPagination, NeonTooltip } from '@ps-refarch-ux/neon';
import { AddSubjectAreaDialog } from './add-subject-area-dialog';
import { DeleteSubjectAreaDialog } from './delete-subject-area-dialog';
import { EditSubjectAreaDialog } from './edit-subject-area-dialog';
import { EMPTY_STATE_CARD_STYLE } from '@/components/EmptyState/empty-state.styles';
import { useToastNotifications } from '@/hooks/use-toast-notifications';
import { DataGrid } from '@/components/DataGrid/data-grid';
import {
  formatScedDisplayText,
  isDuplicateSubjectArea,
  sortSubjectAreas,
  type SubjectAreaSortState,
  toolTipAssociatedCourses,
} from '@/utils/subject-area-utils';
import { usePageConfig } from '@/layout-templates';
import { isCourseCatalogReadOnly } from '@/utils/cp-context';
import { TableSkeleton } from '@/components/TableSkeleton/table-skeleton';
ModuleRegistry.registerModules([AllCommunityModule]);

export const SubjectAreas = (): ReactElement => {
  const { showToast } = useToastNotifications();
  const isReadOnly = isCourseCatalogReadOnly();
  const { data, isLoading, error } = useGetSubjectAreas({
    getLockedStatus: true,
  });
  const createSubjectArea = useCreateSubjectArea();
  const deleteSubjectArea = useDeleteSubjectArea();
  const updateSubjectArea = useUpdateSubjectArea();
  const [currentPage, setCurrentPage] = useState(1);
  const [pageLength, setPageLength] = useState(25);
  const [paginatedRows, setPaginatedRows] = useState<SubjectArea[]>([]);
  const [sortState, setSortState] = useState<SubjectAreaSortState | null>(null);

  const [subjectAreaName, setSubjectAreaName] = useState<string>('');
  const [selectedScedCode, setSelectedScedCode] = useState<string | undefined>(undefined);
  const [showEditModal, setShowEditModal] = useState(false);
  const [subjectAreaToEdit, setSubjectAreaToEdit] = useState<SubjectArea | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [subjectAreaToDelete, setSubjectAreaToDelete] = useState<SubjectArea | null>(null);

  const allSubjectAreas = useMemo(() => data?.data ?? [], [data?.data]);
  const sortedSubjectAreas = useMemo(
    () => sortSubjectAreas(allSubjectAreas, sortState),
    [allSubjectAreas, sortState]
  );
  const shouldShowPagination = sortedSubjectAreas.length > SUBJECT_AREAS_PAGINATION_THRESHOLD;
  const displayedRows = shouldShowPagination ? paginatedRows : sortedSubjectAreas;

  useEffect(() => {
    if (!sortedSubjectAreas.length) {
      setPaginatedRows([]);
      return;
    }
    setCurrentPage(1);
    setPaginatedRows(sortedSubjectAreas.slice(0, pageLength));
  }, [sortedSubjectAreas, pageLength]);

  const handleSortChanged = useCallback((event: SortChangedEvent<SubjectArea>) => {
    const sortedColumn = event.api
      .getColumnState()
      .find(
        (columnState: ColumnState) =>
          columnState.sort !== null &&
          (columnState.colId === 'name' || columnState.colId === 'SCEDSubject')
      );

    if (!sortedColumn?.sort) {
      setSortState(null);
      return;
    }

    setSortState({
      colId: sortedColumn.colId as SubjectAreaSortState['colId'],
      sort: sortedColumn.sort,
    });
  }, []);

  const [showAddSubjectAreaModal, setShowAddSubjectAreaModal] = useState(false);
  const openAddModal = (): void => {
    setShowAddSubjectAreaModal(true);
  };

  usePageConfig({
    breadcrumbs: getSubjectAreasBreadcrumbs,
    breadcrumbsId: SUBJECT_AREAS_ID.Breadcrumbs,
    title: SUBJECT_AREAS_TEXT.Title,
    description: SUBJECT_AREAS_TEXT.Description,
    ...(isReadOnly
      ? {}
      : {
          action: {
            id: SUBJECT_AREAS_ID.Button,
            text: SUBJECT_AREAS_TEXT.Button,
            icon: SUBJECT_AREAS_ICON.Add,
            type: NeonButtonTypes.Primary,
            ariaHasPopup: 'dialog',
            onClick: openAddModal,
          },
        }),
  });

  const closeAddModal = (): void => {
    setShowAddSubjectAreaModal(false);
    setSubjectAreaName('');
    setSelectedScedCode(undefined);
  };

  const openDeleteModal = useCallback((subjectArea: SubjectArea): void => {
    setSubjectAreaToDelete(subjectArea);
    setShowDeleteModal(true);
  }, []);

  const closeDeleteModal = (): void => {
    setShowDeleteModal(false);
    setSubjectAreaToDelete(null);
  };

  const openEditModal = useCallback((subjectArea: SubjectArea): void => {
    setSubjectAreaToEdit(subjectArea);
    setSubjectAreaName(subjectArea.name);
    setSelectedScedCode(String(subjectArea.SCEDSubject));
    setShowEditModal(true);
  }, []);

  const closeEditModal = (): void => {
    setShowEditModal(false);
    setSubjectAreaToEdit(null);
    setSubjectAreaName('');
    setSelectedScedCode(undefined);
  };

  const columnDefs = useMemo<ColDef<SubjectArea>[]>(() => {
    const nameAndScedCellStyle: CellStyle = {
      ...tableColumnsStyle,
      ...createMarginStyle({ marginLeft: '16px' }),
    };
    const columns: ColDef<SubjectArea>[] = [
      {
        field: 'name',
        headerName: 'Subject area',
        flex: 2,
        minWidth: 200,
        headerClass: SUBJECT_AREAS_GRID_HEADER_CLASS,
        cellClass: 'subjectAreasNameCell',
        cellStyle: nameAndScedCellStyle,
        sortable: true,
        comparator: (valueA: string, valueB: string) => {
          return (valueA || '').localeCompare(valueB || '', undefined, { sensitivity: 'case' });
        },
        headerComponent: SortableHeader,
      },
      {
        field: 'SCEDSubject',
        headerName: 'SCED subject code',
        flex: 3,
        minWidth: 250,
        headerClass: SUBJECT_AREAS_GRID_HEADER_CLASS,
        cellStyle: nameAndScedCellStyle,
        sortable: true,
        comparator: (valueA: number, valueB: number) => {
          return (valueA || 0) - (valueB || 0);
        },
        cellRenderer: (props: { data: SubjectArea }) => {
          const scedCode = props.data.SCEDSubject;
          const scedEntry = SCEDSubjectCodes.find((item) => scedCode && item.code === scedCode);
          return scedEntry ? formatScedDisplayText(scedEntry.description) : null;
        },
        headerComponent: (params: IHeaderParams) => (
          <SortableHeader {...params}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <NeonButton
                id="sced-subject-info-tooltip"
                dataText="SCED subject code information"
                dataShowText={false}
                dataType={NeonButtonTypes.Utility}
                dataIcon={'circle-info'}
                dataAriaLabel="SCED subject code information"
              />
              <NeonTooltip dataTargetId="sced-subject-info-tooltip">
                {SUBJECT_AREAS_TEXT.SCEDToolTip}
              </NeonTooltip>
            </div>
          </SortableHeader>
        ),
      },
    ];

    if (!isReadOnly) {
      columns.push({
        colId: 'actions',
        headerName: 'Actions',
        headerClass: SUBJECT_AREAS_GRID_HEADER_CLASS,
        cellStyle: { ...tableColumnsStyle },
        sortable: false,
        cellRenderer: (props: { data: SubjectArea }) => {
          const tooltipKey = (props.data.id ?? `${props.data.name}-${props.data.SCEDSubject}`)
            .toString()
            .toLowerCase()
            .replace(/[^a-z0-9-_]/g, '-');
          const editAndDeleteTooltipTargetId = `neon-edit-${tooltipKey}-tooltip-target`;
          return (
            <div id={editAndDeleteTooltipTargetId} style={actionButtonsContainerStyle}>
              <NeonButton
                id={`neon-edit-${props.data.id}`}
                dataIcon={'edit'}
                dataType="utility"
                dataText="Edit subject area"
                dataShowText={false}
                disabled={props.data.locked}
                dataAriaLabel="Edit subject area"
                dataAriaHaspopup="dialog"
                onClick={(): void => openEditModal(props.data)}
              />
              <NeonButton
                id={`neon-delete-${props.data.id}`}
                dataIcon={'delete'}
                dataType={'utility'}
                dataText="Delete subject area"
                dataShowText={false}
                disabled={props.data.locked}
                dataAriaLabel="Delete subject area"
                dataAriaHaspopup="dialog"
                onClick={(): void => openDeleteModal(props.data)}
              />
              {props.data.locked && (
                <NeonTooltip dataTargetId={editAndDeleteTooltipTargetId}>
                  <span style={{ whiteSpace: 'pre-line' }}>
                    {toolTipAssociatedCourses(props.data.countOfAssociatedCourses ?? 0)}
                  </span>
                </NeonTooltip>
              )}
            </div>
          );
        },
      });
    }

    return columns;
  }, [isReadOnly, openDeleteModal, openEditModal]);

  const handleDeleteSubjectArea = (): void => {
    if (!subjectAreaToDelete) {
      return;
    }

    const payload = {
      subjectArea: subjectAreaToDelete,
    };

    deleteSubjectArea.mutate(payload, {
      onSuccess: () => {
        closeDeleteModal();
        showToast('success', {
          heading: `${subjectAreaToDelete.name} has been deleted`,
        });
      },
      onError: (error) => {
        console.error('Failed to delete subject area:', error);
        showToast('error', {
          heading: `${subjectAreaToDelete.name} subject area failed to delete`,
        });
      },
    });
  };

  const handleSaveSubjectArea = (): void => {
    const normalizedSubjectAreaName = subjectAreaName.trim();
    if (!normalizedSubjectAreaName || !selectedScedCode) {
      console.error('Name and SCED code are required');
      return;
    }

    const payload = {
      subjectArea: {
        name: normalizedSubjectAreaName,
        SCEDSubject: Number(selectedScedCode),
        locked: false,
      },
    };

    createSubjectArea.mutate(payload, {
      onSuccess: () => {
        closeAddModal();
        showToast('success', {
          heading: `${normalizedSubjectAreaName} subject area added`,
        });
      },
      onError: (error) => {
        console.error('Failed to create subject area:', error);
        showToast('error', {
          heading: `${subjectAreaName} subject area failed to save`,
        });
      },
    });
  };

  const handleUpdateSubjectArea = (): void => {
    const normalizedSubjectAreaName = subjectAreaName.trim();
    if (!normalizedSubjectAreaName || !selectedScedCode || !subjectAreaToEdit) {
      console.error('Name and SCED code are required');
      return;
    }

    const payload = {
      oldSubjectArea: subjectAreaToEdit,
      newSubjectArea: {
        ...subjectAreaToEdit,
        name: normalizedSubjectAreaName,
        SCEDSubject: Number(selectedScedCode),
      },
    };

    updateSubjectArea.mutate(payload, {
      onSuccess: () => {
        closeEditModal();
        showToast('success', {
          heading: `${normalizedSubjectAreaName} subject area updated`,
        });
      },
      onError: (error) => {
        console.error('Failed to update subject area:', error);
        showToast('error', {
          heading: `${subjectAreaName} subject area failed to update`,
        });
      },
    });
  };
  const scedOptions = useMemo(
    () =>
      SCEDSubjectCodes.map((item) => ({
        text: item.description,
        value: String(item.code),
      })),
    []
  );

  const existingSubjectAreas = useMemo(() => allSubjectAreas, [allSubjectAreas]);
  const isDuplicateAdd = useMemo(
    () => isDuplicateSubjectArea(subjectAreaName, Number(selectedScedCode), existingSubjectAreas),
    [existingSubjectAreas, subjectAreaName, selectedScedCode]
  );
  const isDuplicateEdit = useMemo(
    () =>
      subjectAreaToEdit !== null
        ? isDuplicateSubjectArea(
            subjectAreaName,
            Number(selectedScedCode),
            existingSubjectAreas,
            subjectAreaToEdit.id
          )
        : false,
    [existingSubjectAreas, subjectAreaName, selectedScedCode, subjectAreaToEdit]
  );

  return (
    <>
      <div style={sectionSpacingStyle}>
        <div style={contentStackStyle}>
          <div style={infoRowStyle}>
            <NeonIcon dataIcon={SUBJECT_AREAS_ICON.Info} />
            <p
              style={paragraphStyle(
                neonFontSizeVar('font-size-2', '14px'),
                neonLineHeightVar('line-height-2', '20px')
              )}
            >
              {SUBJECT_AREAS_TEXT.Info}
            </p>
          </div>
          {error && (
            <NeonCardStandard>
              <div data-slot={DATA_SLOT} style={EMPTY_STATE_CARD_STYLE}>
                <NeonErrorPageContent dataErrorCode={'500'} />
              </div>
            </NeonCardStandard>
          )}
          {!error && data && data.total === 0 && (
            <EmptyState
              {...(isReadOnly ? {} : { onAction: openAddModal, actionAriaHaspopup: 'dialog' })}
            />
          )}
          {!isLoading && !error && data && data.total > 0 ? (
            <>
              <DataGrid<SubjectArea>
                id="subject-areas"
                columnDefs={columnDefs}
                rowData={displayedRows}
                domLayout="normal"
                rowHeight={50}
                headerHeight={50}
                gridHeight="min(600px, 60vh)"
                cardWrapper={true}
                childrenInsideCard={true}
                className="neon-data-grid subjectAreasGrid plans-of-study-grid subject-areas-grid"
                gridOptions={{
                  enableBrowserTooltips: true,
                  tooltipShowDelay: 200,
                  onSortChanged: handleSortChanged,
                }}
              >
                {shouldShowPagination && (
                  <NeonPagination
                    id="subject-areas-pagination"
                    dataArrayLength={data.total}
                    dataCurrentPage={currentPage}
                    dataPageLength={pageLength}
                    neonPaginationChange={(newPage, newPageLength, getPaginatedData) => {
                      setCurrentPage(newPage);
                      setPageLength(newPageLength);
                      setPaginatedRows(getPaginatedData(sortedSubjectAreas));
                    }}
                  />
                )}
              </DataGrid>
            </>
          ) : (
            !error &&
            isLoading &&
            !data && (
              <NeonCardStandard>
                <div data-slot={DATA_SLOT}>
                  <TableSkeleton
                    columns={SUBJECT_AREAS_SKELETON_COLUMNS}
                    testId="subject-areas-skeleton"
                    ariaLabel="Loading subject areas"
                  />
                </div>
              </NeonCardStandard>
            )
          )}
        </div>
      </div>

      <AddSubjectAreaDialog
        isOpen={showAddSubjectAreaModal}
        subjectAreaName={subjectAreaName}
        selectedScedCode={selectedScedCode}
        scedOptions={scedOptions}
        isSaving={createSubjectArea.isPending}
        isDuplicate={isDuplicateAdd}
        onSubjectAreaNameChange={setSubjectAreaName}
        onSelectedScedCodeChange={setSelectedScedCode}
        onSave={handleSaveSubjectArea}
        onClose={closeAddModal}
      />

      <DeleteSubjectAreaDialog
        isOpen={showDeleteModal}
        subjectAreaToDelete={subjectAreaToDelete}
        isDeleting={deleteSubjectArea.isPending}
        onDelete={handleDeleteSubjectArea}
        onClose={closeDeleteModal}
      />

      <EditSubjectAreaDialog
        isOpen={showEditModal}
        subjectAreaName={subjectAreaName}
        selectedScedCode={selectedScedCode}
        scedOptions={scedOptions}
        isUpdating={updateSubjectArea.isPending}
        originalSubjectArea={subjectAreaToEdit}
        isDuplicate={isDuplicateEdit}
        onSubjectAreaNameChange={setSubjectAreaName}
        onSelectedScedCodeChange={setSelectedScedCode}
        onSave={handleUpdateSubjectArea}
        onClose={closeEditModal}
      />
    </>
  );
};
